import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import { DataGrid } from "@mui/x-data-grid";
import PropTypes from "prop-types";
import RowServiceChargeActionsMenu from "../ServiceTypes/RowServiceChargeActionsMenu";

const definitions = {
  types: {
    title: "STUDENT SERVICE TYPES",
    id: "service_type_id",
    fields: [
      ["service_type", "Service Type", true],
      ["service_type_description", "Description", false],
      ["status", "Status", true],
    ],
  },
  names: {
    title: "STUDENT SERVICE NAMES",
    id: "service_name_id",
    fields: [
      ["service_type_id", "Service Type", true],
      ["service_name", "Service Name", false],
      ["service_name_description", "Description", false],
      ["status", "Status", true],
    ],
  },
  charges: {
    title: "STUDENT SERVICE CHARGES",
    id: "service_charge_id",
    fields: [
      ["acyear_id", "Academic Year", true],
      ["service_type_id", "Service Type", true],
      ["service_name", "Service Name", true],
      ["amount", "Amount", true],
      ["status", "Status", true],
    ],
  },
};

export default function StudentServiceCrud({ kind }) {
  const config = definitions[kind];
  const user = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem("user"));
    } catch {
      return null;
    }
  }, []);
  const apiRoot = process.env.REACT_APP_API_BASE_URL;
  const api = `${apiRoot}/api/student-services/${kind}/${user?.client_id}/${user?.college_id}`;
  const [rows, setRows] = useState([]),
    [types, setTypes] = useState([]),
    [years, setYears] = useState([]),
    [serviceNames, setServiceNames] = useState([]);
  const [dialog, setDialog] = useState(false),
    [editing, setEditing] = useState(null),
    [form, setForm] = useState({ status: "ACTIVE" });
  const [error, setError] = useState(""),
    [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const headers = () => ({ Authorization: `Bearer ${sessionStorage.getItem("token")}` });
  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const res = await axios.get(api, { headers: headers() });
      setRows(res.data?.data || []);
      setError("");
    } catch (e) {
      setError(e.response?.data?.message || "Unable to load records");
    } finally {
      setLoading(false);
    }
  }, [api, user]);
  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    if (!user || kind === "types") return;
    axios
      .get(`${apiRoot}/api/student-services/types/${user.client_id}/${user.college_id}`, {
        headers: headers(),
      })
      .then((r) => setTypes(r.data?.data || []))
      .catch(() => setTypes([]));
    if (kind === "charges")
      axios
        .get(`${apiRoot}/api/acyear/by-college/${user.client_id}/${user.college_id}`, {
          headers: headers(),
        })
        .then((r) => setYears(r.data?.data || []))
        .catch(() => setYears([]));
    if (kind === "charges")
      axios
        .get(`${apiRoot}/api/student-services/names/${user.client_id}/${user.college_id}`, {
          headers: headers(),
        })
        .then((r) => setServiceNames(r.data?.data || []))
        .catch(() => setServiceNames([]));
  }, [apiRoot, kind, user]);
  const openCreate = () => {
    setEditing(null);
    setForm({ status: "ACTIVE" });
    setError("");
    setDialog(true);
  };
  const openEdit = (row) => {
    setEditing(row);
    setForm(Object.fromEntries(config.fields.map(([f]) => [f, row[f] ?? ""])));
    setError("");
    setDialog(true);
  };
  const save = async () => {
    if (
      config.fields
        .filter(([, , required]) => required)
        .some(([field]) => form[field] === "" || form[field] == null)
    ) {
      setError("Complete all required fields before saving.");
      return;
    }
    const payload = {
      ...form,
      created_by: user?.user_email || "system",
      modified_by: user?.user_email || "system",
    };
    try {
      if (editing) await axios.put(`${api}/${editing[config.id]}`, payload, { headers: headers() });
      else await axios.post(api, payload, { headers: headers() });
      setDialog(false);
      await load();
    } catch (e) {
      setError(e.response?.data?.message || "Unable to save record");
    }
  };
  const remove = async (row) => {
    if (!window.confirm("Delete this record?")) return;
    try {
      await axios.delete(`${api}/${row[config.id]}`, { headers: headers() });
      await load();
    } catch (e) {
      setError(e.response?.data?.message || "Unable to delete record");
    }
  };
  const columns = [
    {
      field: "sno",
      headerName: "S.No",
      width: 72,
      sortable: false,
      filterable: false,
      renderCell: (params) => page * pageSize + params.api.getRowIndex(params.id) + 1,
    },
    { field: config.id, headerName: "ID", width: 82 },
    ...config.fields.map(([field, label]) => ({
      field,
      headerName: label,
      flex: 1,
      minWidth: 120,
      valueGetter: (params) => {
        const value = params.row[field];
        if (field === "service_type_id") {
          return (
            types.find((t) => String(t.service_type_id) === String(value))?.service_type || value
          );
        }
        if (field === "acyear_id") {
          return years.find((y) => String(y.acyear_id) === String(value))?.academic_year || value;
        }
        return value ?? "";
      },
    })),
    {
      field: "actions",
      headerName: "Actions",
      width: 90,
      sortable: false,
      renderCell: ({ row }) => (
        <RowServiceChargeActionsMenu
          row={{ ...row, service_charge_id: row[config.id] }}
          onEdit={openEdit}
          onDelete={(id) => remove({ [config.id]: id })}
          hasPermission={() => true}
        />
      ),
    },
  ];
  const input = ([field, label]) => {
    const shared = {
      key: field,
      label,
      InputLabelProps: { shrink: true },
      fullWidth: true,
      value: form[field] ?? "",
      onChange: (e) =>
        setForm((p) => ({
          ...p,
          [field]: e.target.value,
          ...(kind === "charges" && field === "service_type_id" ? { service_name: "" } : {}),
        })),
    };
    if (field === "status")
      return (
        <TextField
          {...shared}
          select
          SelectProps={{ native: true }}
          InputLabelProps={{ shrink: true }}
        >
          <option value="">Select Status</option>
          <option value="ACTIVE">ACTIVE</option>
          <option value="INACTIVE">INACTIVE</option>
        </TextField>
      );

    if (field === "service_type_id")
      return (
        <TextField
          {...shared}
          select
          SelectProps={{ native: true }}
          InputLabelProps={{ shrink: true }}
        >
          <option value="">Select service type</option>
          {types.map((t) => (
            <option key={t.service_type_id} value={t.service_type_id}>
              {t.service_type}
            </option>
          ))}
        </TextField>
      );

    if (field === "acyear_id")
      return (
        <TextField
          {...shared}
          select
          SelectProps={{ native: true }}
          InputLabelProps={{ shrink: true }}
        >
          <option value="">Select academic year</option>
          {years.map((y) => (
            <option key={y.acyear_id} value={y.acyear_id}>
              {y.academic_year}
            </option>
          ))}
        </TextField>
      );

    if (field === "service_name" && kind === "charges") {
      const availableServiceNames = serviceNames.filter(
        (name) =>
          String(name.service_type_id) === String(form.service_type_id) &&
          name.status === "ACTIVE" &&
          name.service_name
      );

      return (
        <TextField
          {...shared}
          select
          disabled={!form.service_type_id}
          SelectProps={{ native: true }}
          InputLabelProps={{ shrink: true }}
        >
          <option value="">Select service name</option>

          {form.service_name &&
            !availableServiceNames.some((name) => name.service_name === form.service_name) && (
              <option value={form.service_name}>{form.service_name}</option>
            )}

          {availableServiceNames.map((name) => (
            <option key={name.service_name_id} value={name.service_name}>
              {name.service_name}
            </option>
          ))}
        </TextField>
      );
    }
    return (
      <TextField
        {...shared}
        type={field === "amount" ? "number" : "text"}
        inputProps={{
          maxLength:
            field.includes("description") || field === "service_type" || field === "service_name"
              ? kind === "charges"
                ? 30
                : 50
              : undefined,
          min: field === "amount" ? 0 : undefined,
        }}
      />
    );
  };
  return (
    <Box p={1.5}>
      <Paper sx={{ p: 1.5 }}>
        <Stack direction="row" alignItems="center" mb={1}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            {config.title}
          </Typography>
          <Button
            size="small"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreate}
            sx={{ color: "#fff" }}
          >
            Add New
          </Button>
        </Stack>
        {error && !dialog && (
          <Typography color="error" mb={1}>
            {error}
          </Typography>
        )}
        <Box sx={{ height: 430, width: "100%" }}>
          <DataGrid
            rows={rows}
            columns={columns}
            getRowId={(row) => row[config.id]}
            loading={loading}
            density="compact"
            page={page}
            onPageChange={setPage}
            pageSize={pageSize}
            onPageSizeChange={(nextPageSize) => {
              setPageSize(nextPageSize);
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
          />
        </Box>
      </Paper>
      <Dialog open={dialog} onClose={() => setDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          {editing ? "Edit" : "Add"} {config.title}
          <IconButton
            aria-label="Close"
            onClick={() => setDialog(false)}
            sx={{ position: "absolute", right: 8, top: 8, color: "error.main" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {config.fields.map(input)}
            {error && <Typography color="error">{error}</Typography>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="error"
            startIcon={<CloseIcon />}
            onClick={() => setDialog(false)}
            sx={{ color: "#fff", backgroundColor: "#d32f2f" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={save}
            sx={{ color: "#fff", backgroundColor: "#1976d2" }}
          >
            {editing ? "Update" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

StudentServiceCrud.propTypes = {
  kind: PropTypes.oneOf(["types", "names", "charges"]).isRequired,
};
